import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import DealScore from '../components/ProductDetails/DealScore';
import { parseProductUrl, storeInfo } from '../utils/slugify';
import { buyNowProduct } from '../utils/buyApi';
import API from '../api/apiConfig';

const ProductDetailsPage = () => {
  const { productSlug } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [productData, setProductData] = useState(null);
  const [storeComparison, setStoreComparison] = useState([]);

  // Extract IDs from slug
  const parsed = parseProductUrl(productSlug);
  const { sid, pid } = parsed || {};

  useEffect(() => {
    const fetchDetails = async () => {
      if (!sid || !pid) {
        setLoading(false);
        return;
      }

      setLoading(true);
      console.log('Fetching product details for:', { sid, pid });
      try {
        const response = await API.post(`/api/get-product-details`, {
          sid: Number(sid),
          pid: pid
        });

        console.log('Product Details API Raw Response:', response.data);
        const product = response.data.data;
        if (product && product.pid) {
          setProductData(product);
          // Standardizing graph data for DealScore if needed
          setStoreComparison(response.data.storeComparison || []);
        } else {
          console.warn('No product details found in response data');
        }
      } catch (error) {
        console.error('Error fetching product details:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchDetails();
  }, [productSlug, sid, pid]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAFB] flex items-center justify-center font-inter">
        <div className="flex flex-col items-center">
          <div className="w-12 h-12 border-4 border-[#7E54F2]/20 border-t-[#7E54F2] rounded-full animate-spin"></div>
          <h3 className="text-lg font-bold text-slate-800 text-center uppercase tracking-wider">Deal Score Analysis</h3>
        </div>
      </div>
    );
  }

  if (!productData || !parsed) {
    return (
      <div className="min-h-screen bg-[#FAFAFB] py-20 px-4 text-center font-inter">
        <div className="w-20 h-20 bg-white rounded-[32px] shadow-xl flex items-center justify-center mx-auto mb-8">
          <span className="text-3xl">⚠️</span>
        </div>
        <h1 className="text-2xl font-black text-gray-900 mb-2">Product Not Found</h1>
        <p className="text-gray-400 mb-10 text-sm font-medium">The link might be broken or the product is no longer tracked.</p>
        <button
          onClick={() => navigate('/amazon-deals')}
          className="bg-[#7E54F2] text-white px-8 py-3 rounded-2xl font-black text-sm uppercase tracking-widest shadow-lg hover:shadow-xl transition-all active:scale-95"
        >
          Explore Other Deals
        </button>
      </div>
    );
  }

  const { title, price, mrp, imgurl, brand } = productData;
  const savings = mrp > price ? Math.round(((mrp - price) / mrp) * 100) : 0;
  const storeMeta = storeInfo[sid] || { label: "Retailer" };

  return (
    <div className="bg-[#FAFAFB] min-h-screen pt-0 pb-10 px-4 font-inter">
      <div className="max-w-[1280px] mx-auto mt-2 md:mt-4 flex flex-col gap-8">

        {/* Compact Hero Layout */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 items-start">

          <div className="md:col-span-12 lg:col-span-5 xl:col-span-4">
            {/* Left Side: Premium Product Visuals */}
            <div className="relative group">
              {/* Multi-layered soft background accents */}
              <div className="absolute inset-0 bg-[#7E54F2]/[0.02] rounded-[48px] -rotate-1 lg:scale-105 transition-transform group-hover:rotate-0 duration-700"></div>
              <div className="absolute -top-10 -right-10 w-64 h-64 bg-violet-100/30 rounded-full blur-[80px] -z-10 animate-pulse"></div>
              <div className="absolute -bottom-10 -left-10 w-48 h-48 bg-purple-100/30 rounded-full blur-[60px] -z-10 animate-pulse" style={{ animationDelay: '1s' }}></div>

              <div className="bg-white rounded-[40px] shadow-[0_20px_80px_rgba(0,0,0,0.04)] border border-black/[0.03] p-10 lg:p-14 transition-all duration-700 hover:shadow-[0_40px_120px_rgba(126,84,242,0.08)] relative overflow-hidden h-full flex items-center justify-center">
                {/* Product Badge - Floating Glassmorphism */}
                {savings > 0 && (
                  <div className="absolute top-8 left-8 z-20">
                    <div className="relative">
                      <div className="absolute inset-0 bg-[#7E54F2] blur-xl opacity-20 animate-pulse"></div>
                      <div className="bg-[#7E54F2] text-white w-14 h-14 md:w-16 md:h-16 flex flex-col items-center justify-center rounded-full shadow-2xl relative border-2 border-white/20">
                        <span className="text-sm md:text-base font-black leading-none">{savings}%</span>
                        <span className="text-[8px] md:text-[10px] font-bold uppercase tracking-tighter opacity-80">Off</span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Store Tag - Minimalist Glass */}
                <div className="absolute bottom-8 right-8 z-20">
                  <div className="bg-white/70 backdrop-blur-md border border-black/5 px-5 py-2.5 rounded-2xl flex items-center gap-3 shadow-sm hover:shadow-md transition-all cursor-default">
                    <img src={storeMeta.favIcon} alt={storeMeta.label} className="h-4 w-auto grayscale group-hover:grayscale-0 transition-all duration-500" />
                    <span className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-500">{storeMeta.label}</span>
                  </div>
                </div>

                {/* Main Product Image */}
                <div className="relative w-full aspect-[4/3.2] md:max-h-[450px] flex items-center justify-center">
                  <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-[#7E54F2]/[0.01] to-transparent rounded-full rotate-45"></div>
                  <img
                    src={imgurl}
                    alt={title}
                    className="w-full h-full object-contain relative z-10 transition-transform duration-700 group-hover:scale-105 group-hover:-rotate-1"
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="md:col-span-12 lg:col-span-7 xl:col-span-8 flex flex-col gap-6">
            <div className="bg-white p-10 md:p-14 rounded-[24px] shadow-[0_4px_20px_rgb(0,0,0,0.015)] border border-gray-100 relative overflow-hidden h-full flex flex-col">
              <div className="absolute top-0 right-0 w-64 h-64 bg-[#7E54F2]/5 rounded-full blur-3xl -z-0"></div>

              {brand && (
                <div className="flex items-center gap-2 mb-6 relative z-10">
                  <span className="text-[9px] font-black uppercase tracking-[0.3em] text-[#7E54F2] bg-[#7E54F2]/5 px-4 py-1.5 rounded-lg border border-[#7E54F2]/10">
                    {brand}
                  </span>
                </div>
              )}

              <h1 className="text-lg md:text-xl font-semibold text-slate-800 mb-6 leading-relaxed tracking-tight relative z-10">
                {title}
              </h1>

              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-6 relative z-10 pt-6 border-t border-gray-50">
                <div className="flex flex-col gap-2">
                  <span className="text-[9px] text-gray-400 font-bold uppercase tracking-[0.3em]">Deal Price</span>
                  <div className="flex items-baseline gap-3">
                    <span className="text-2xl md:text-3xl font-semibold text-slate-900 tracking-tight">₹{price.toLocaleString()}</span>
                    {mrp > price && (
                      <span className="text-sm md:text-base text-gray-300 font-medium line-through decoration-gray-200">₹{mrp.toLocaleString()}</span>
                    )}
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-3 mt-auto pt-10 border-t border-gray-50">
                  <button
                    onClick={() => buyNowProduct(sid, pid)}
                    className="flex-grow bg-[#7E54F2] text-white py-4 px-8 rounded-2xl font-black text-[10px] md:text-xs uppercase tracking-[0.25em] shadow-lg hover:shadow-[#7E54F2]/20 hover:-translate-y-0.5 transition-all active:scale-95 flex items-center justify-center gap-3 whitespace-nowrap"
                  >
                    <span>Buy Now</span>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M14 5l7 7-7 7M5 12h14" /></svg>
                  </button>

                  <button
                    onClick={() => {
                      const shareText = `🔥 Check out this deal on The Best Deals!\n\n📦 ${title}\n💰 Just ₹${price.toLocaleString("en-IN")}\n\n👉 View here: ${window.location.href}`;
                      window.open(`https://api.whatsapp.com/send?text=${encodeURIComponent(shareText)}`, "_blank");
                    }}
                    className="flex-grow flex items-center justify-center gap-2 border border-gray-200 bg-white text-[#25D366] font-bold text-[13px] md:text-sm hover:bg-gray-50 transition-colors py-3 px-6 rounded-xl shadow-sm whitespace-nowrap"
                    title="Share on WhatsApp"
                  >
                    <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                    </svg>
                    <span>Share on WhatsApp</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Section: Advanced Analytics & Store Comparison - Full Width */}
        <DealScore productData={productData} storeComparison={storeComparison} />
      </div>
    </div>
  );
};

export default ProductDetailsPage;
